import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
// frontend/src/pages/maintenance/AppointmentsList.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  Sheet,
  Chip,
  Input,
  Select,
  Option,
  Stack,
  CircularProgress,
  IconButton,
} from '@mui/joy';
const PAGE_SIZE = 20;
import { MagnifyingGlass as Search, Funnel as Filter, Calendar, MapPin, CaretRight as ChevronRight, X, Image, Wrench } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { appointmentApi } from '../../lib/maintenanceApi';
import type { AppointmentStatus, AppointmentFilters } from '../../types/maintenance';
import { APPOINTMENT_STATUS_LABELS, getStatusColor, formatDuration } from '../../types/maintenance';

interface AppointmentsListProps {
  embedded?: boolean;
}

const AppointmentsList: React.FC<AppointmentsListProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | ''>('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFilter, searchQuery]);

  // Build filters
  const filters: AppointmentFilters = { page: currentPage };
  if (statusFilter) filters.status = statusFilter;
  if (dateFilter) filters.scheduled_date = dateFilter;

  // Fetch appointments
  const { data: appointmentsResponse, isLoading } = useQuery({
    queryKey: ['maintenance-appointments', filters],
    queryFn: () => appointmentApi.list(filters),
  });

  const appointments = appointmentsResponse?.results || [];
  const totalCount = appointmentsResponse?.count ?? 0;

  // Filter by search query
  const filteredAppointments = appointments.filter((appt) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      appt.contract_number.toLowerCase().includes(query) ||
      appt.contact_name.toLowerCase().includes(query) ||
      appt.property_address.toLowerCase().includes(query)
    );
  });

  return (
    <Box sx={embedded ? {} : { p: 3 }}>
      {/* Header - only show when not embedded */}
      {!embedded && (
        <Box sx={{ mb: 3 }}>
          <Typography level="h2">Appointments</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Manage maintenance appointments
          </Typography>
        </Box>
      )}

      {/* Filters */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Input
              placeholder="Search..."
              startDecorator={<Search size={18} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 250 }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value as AppointmentStatus | '')}
              startDecorator={<Filter size={18} />}
              sx={{ minWidth: 150 }}
            >
              <Option value="">All Statuses</Option>
              {Object.entries(APPOINTMENT_STATUS_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              startDecorator={<Calendar size={18} />}
              sx={{ minWidth: 180 }}
            />
            {(searchQuery || statusFilter || dateFilter) && (
              <IconButton
                variant="plain"
                color="neutral"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                  setDateFilter('');
                }}
              >
                <X size={18} />
              </IconButton>
            )}
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Appointments Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredAppointments.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Calendar size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <Typography level="h4" sx={{ mb: 1 }}>
              No appointments found
            </Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              {searchQuery || statusFilter || dateFilter
                ? 'Try adjusting your filters'
                : 'Schedule appointments from contract pages'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
          <Table hoverRow stickyHeader>
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Property</th>
                <th>Status</th>
                <th>Technician</th>
                <th>Duration</th>
                <th>Photos</th>
                <th>Repairs</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map((appt) => (
                <tr
                  key={appt.id}
                  onClick={() => navigate(`/maintenance/appointments/${appt.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {appt.scheduled_date}
                    </Typography>
                    {appt.scheduled_time_start && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {appt.scheduled_time_start}
                      </Typography>
                    )}
                  </td>
                  <td>{appt.contact_name}</td>
                  <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MapPin size={14} />
                      <Typography level="body-sm" sx={{ maxWidth: 200 }} noWrap>
                        {appt.property_address}
                      </Typography>
                    </Box>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      sx={{
                        bgcolor: `${getStatusColor(appt.status)}20`,
                        color: getStatusColor(appt.status),
                      }}
                    >
                      {APPOINTMENT_STATUS_LABELS[appt.status]}
                    </Chip>
                  </td>
                  <td>{appt.assigned_to_name || 'Unassigned'}</td>
                  <td>{formatDuration(appt.duration_minutes)}</td>
                  <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Image size={14} />
                      {appt.photo_count}
                    </Box>
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Wrench size={14} />
                      {appt.repair_item_count}
                    </Box>
                  </td>
                  <td>
                    <IconButton size="sm" variant="plain">
                      <ChevronRight size={18} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Pagination */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography level="body-sm" color="neutral">
          {totalCount > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} appointments`
            : 'No appointments'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AppointmentsList;
